import React, { useEffect, useMemo, useRef, useState } from 'react'

import { View } from '@amzn/stencil-react-components/layout'
import {
    Controls,
    deserializeFromMarkdown,
    RichTextEditor,
    RichTextEditorProps,
    useEditor,
} from '@amzn/stencil-react-components/rte'
import { TextArea } from '@amzn/stencil-react-components/text-area'

import { Locale } from 'src/models/dto/Locale'

export type ToolbarControls = RichTextEditorProps['controls']

export const SimpleToolbar: ToolbarControls = {
    blockquote: false,
    bulletList: false,
    underline: false,
    link: false,
    heading: false,
    horizontalRule: false,
    keyboardShortcuts: false,
    fullScreen: false,
    clearFormat: false,
    orderedList: false,
    strike: false,
    history: false,
    textAlign: false,
    indent: false,
}

export interface MarkdownEditorProps {
    disabled?: boolean
    title?: string
    value: string
    onChange: React.Dispatch<string>
    id?: string
    dataTestId?: string
    fallbackToTextArea?: boolean
    height?: string | number
    locale?: Locale
    placeholder?: string
    toolbarControls?: ToolbarControls
}

const defaultHeight = '320px'

export function StencilMarkdownEditor({
    value: savedValue,
    onChange: onValueChange,
    id,
    dataTestId,
    height = defaultHeight,
    toolbarControls,
    disabled,
}: MarkdownEditorProps) {
    const lastValue = useRef(savedValue)
    // deserialize from markdown will keep backwards functionality for when we have markdown in the cell
    const [content, setContent] = useState(() => deserializeFromMarkdown(savedValue ?? ''))

    const controls = useMemo(
        (): Controls =>
            ({
                ...toolbarControls,
                fullScreen: false,
            } as never),
        [toolbarControls]
    )
    const [editorProps, editorInstance] = useEditor(
        useMemo(
            () => ({
                content,
                controls,
            }),
            [content, controls]
        )
    )

    useEffect(() => {
        if (savedValue === lastValue.current) {
            return
        }
        lastValue.current = savedValue
        setContent(savedValue ?? '')
    }, [savedValue])

    useEffect(() => {
        if (!editorInstance) {
            return
        }
        const update = () => {
            const html = editorInstance?.getHTML() ?? ''
            if (html === lastValue.current) {
                return
            }

            lastValue.current = html
            onValueChange?.(html)
        }

        editorInstance.on('blur', update)
        editorInstance.on('update', update)
        return () => {
            editorInstance.off('blur', update)
            editorInstance.off('update', update)
        }
    }, [onValueChange, editorInstance])

    return (
        <View id={id} dataTestId={dataTestId} style={{ minHeight: height }}>
            <RichTextEditor {...editorProps} content={content} disabled={disabled} />
        </View>
    )
}

function TextAreaMarkdownEditor({
    value: markdownControlled,
    onChange: onMarkdownChange,
    id,
    dataTestId,
    height = defaultHeight,
    placeholder,
    disabled,
}: MarkdownEditorProps) {
    return (
        <TextArea
            placeholder={placeholder ?? 'Markdown Content'}
            value={markdownControlled}
            onChange={(e) => onMarkdownChange(e.target.value)}
            dataTestId={dataTestId}
            id={id}
            height={height}
            disabled={disabled}
        />
    )
}

export function MarkdownEditor(props: MarkdownEditorProps) {
    const { fallbackToTextArea } = props
    const canRenderMarkdownEditor =
        (window as never as Record<string, unknown>).ResizeObserver &&
        (document as never as Record<string, unknown>).elementFromPoint
    if (!canRenderMarkdownEditor || fallbackToTextArea) {
        return <TextAreaMarkdownEditor {...props} />
    }
    /* istanbul ignore next */
    return <StencilMarkdownEditor {...props} />
}
